import { useEffect, useMemo, useState } from 'react'
import api from '../api/axios'
import { ExclamationTriangleIcon, ArrowPathIcon, MagnifyingGlassIcon, BugAntIcon } from '@heroicons/react/24/outline'

interface ErrorReport {
  id: string
  company_id: string
  company_name?: string
  company_code?: string
  user_id?: string
  employee_name?: string
  message: string
  route?: string
  app_version?: string
  platform?: string
  status: 'new' | 'reviewed' | 'resolved' | 'ignored'
  created_at: string
}

const STATUS_STYLES: Record<ErrorReport['status'], string> = {
  new: 'bg-red-100 text-red-700',
  reviewed: 'bg-amber-100 text-amber-700',
  resolved: 'bg-emerald-100 text-emerald-700',
  ignored: 'bg-slate-100 text-slate-600',
}

export default function ErrorReports() {
  const [reports, setReports] = useState<ErrorReport[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [searchText, setSearchText] = useState('')
  const [updatingId, setUpdatingId] = useState<string | null>(null)

  useEffect(() => {
    fetchReports()
  }, [statusFilter])

  const fetchReports = async () => {
    setLoading(true)
    setError('')
    try {
      const response = await api.get('/super-admin/error-reports', {
        params: {
          status: statusFilter !== 'all' ? statusFilter : undefined,
          limit: 200,
        },
      })
      setReports(response.data.reports || [])
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load error reports')
    } finally {
      setLoading(false)
    }
  }

  const updateStatus = async (id: string, status: ErrorReport['status']) => {
    setUpdatingId(id)
    try {
      await api.patch(`/super-admin/error-reports/${id}/status`, { status })
      setReports((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)))
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update report status')
    } finally {
      setUpdatingId(null)
    }
  }

  const filteredReports = useMemo(() => {
    const text = searchText.toLowerCase()
    return reports.filter((r) => {
      if (text) {
        const blob = [r.company_name, r.company_code, r.employee_name, r.message, r.route]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        if (!blob.includes(text)) return false
      }
      return true
    })
  }, [reports, searchText])

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <BugAntIcon className="w-7 h-7 text-red-600" />
              Error Reports
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">Issues employees flagged from the mobile app (latest 200)</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchReports} disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50">
              <ArrowPathIcon className="w-4 h-4" /> {loading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {error && (
          <div className="flex items-center gap-3 p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
            <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0" /> {error}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input type="text" placeholder="Search company, employee, message, route" value={searchText} onChange={(e) => setSearchText(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none">
            <option value="all">All statuses</option>
            <option value="new">New</option>
            <option value="reviewed">Reviewed</option>
            <option value="resolved">Resolved</option>
            <option value="ignored">Ignored</option>
          </select>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center"><div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-3" /><p className="text-sm text-slate-500">Loading error reports…</p></div>
          </div>
        ) : filteredReports.length === 0 ? (
          <div className="text-center py-16">
            <BugAntIcon className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-400">No error reports found.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  {['When', 'Company', 'Employee', 'Message', 'Context', 'Status'].map(h => (
                    <th key={h} className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredReports.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-slate-900">{r.company_name || 'Unknown'}</div>
                      <div className="text-xs text-slate-400">{r.company_code}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">{r.employee_name || 'Unknown'}</td>
                    <td className="px-4 py-3 max-w-xs">
                      <div className="text-sm text-slate-700">{r.message}</div>
                    </td>
                    <td className="px-4 py-3">
                      {r.route && <div className="text-xs text-slate-500 font-mono">{r.route}</div>}
                      <div className="text-xs text-slate-400">{[r.platform, r.app_version].filter(Boolean).join(' · ')}</div>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={r.status}
                        disabled={updatingId === r.id}
                        onChange={(e) => updateStatus(r.id, e.target.value as ErrorReport['status'])}
                        className={`text-[11px] font-semibold rounded-full px-2 py-0.5 border-0 outline-none cursor-pointer disabled:opacity-50 ${STATUS_STYLES[r.status]}`}
                      >
                        <option value="new">New</option>
                        <option value="reviewed">Reviewed</option>
                        <option value="resolved">Resolved</option>
                        <option value="ignored">Ignored</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
